import { Timestamp } from 'firebase/firestore';

export enum ActionType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  VIEW = 'view',
  LOGIN = 'login',
  LOGOUT = 'logout',
  PASSWORD_RESET = 'password_reset',
  INVITE_SENT = 'invite_sent',
  PERMISSION_CHANGE = 'permission_change',
  EXPORT_DATA = 'export_data',
  BULK_UPDATE = 'bulk_update',
  ACTIVATE = 'activate',
  DEACTIVATE = 'deactivate',
  RESERVE = 'reserve',
  PRE_RESERVED = 'pre_reserved',
  CANCEL = 'cancel',
  DEAL_CAPTURE = 'deal_capture',
  DEAL_REVIEW = 'deal_review',
  DEAL_APPROVE = 'deal_approve',
  DEAL_REJECT = 'deal_reject',
  DEAL_UPDATE_STATUS = 'deal_update_status',
  UNIT_ALLOCATE = 'unit_allocate',
  UNIT_DEALLOCATE = 'unit_deallocate',
  UNIT_REALLOCATE = 'unit_reallocate',
  RESTORE = 'restore',
  COMMISSION_PAID = 'commission_paid'
}

export enum DealStatus {
  // Initial stages
  AVAILABLE = 'available',
  PRE_RESERVED = 'PRE_RESERVED',
  RESERVED = 'RESERVED',
  
  // OTP stages
  OTP_UPLOADED_PENDING = 'OTP_UPLOADED_PENDING',
  OTP_AWAITING_SELLER = 'OTP_AWAITING_SELLER',
  OTP_AWAIT_FINANCIALS = 'OTP_AWAIT_FINANCIALS',
  OTP_CASH_DEAL = 'OTP_CASH_DEAL',
  
  // Finance stages
  FINANCE_AT_BANK = 'FINANCE_AT_BANK',
  FINANCE_AIP = 'FINANCE_AIP', // Approval in Principle
  FINANCE_FINAL_GRANT = 'FINANCE_FINAL_GRANT',
  FINANCE_ACCEPTED = 'FINANCE_ACCEPTED',
  FINANCE_DECLINED = 'FINANCE_DECLINED',
  
  // Completion stages
  COMPLETED_AWAITING_DEPOSIT = 'COMPLETED_AWAITING_DEPOSIT',
  COMPLETED_100_PERCENT = 'COMPLETED_100_PERCENT',
  
  // Transfer stages
  READY_TO_LODGE = 'READY_TO_LODGE',
  TRANSFERRED = 'TRANSFERRED',

  // Commission stage
  COMMISSION_RECEIVED = 'COMMISSION_RECEIVED',

  // Special status
  CANT_BE_SAVED = 'CANT_BE_SAVED',
  
  // Legacy/Additional stages (keeping for backward compatibility)
  OTP_UPLOADED = 'OTP_UPLOADED',
  OTP_PENDING_REVIEW = 'OTP_PENDING_REVIEW',
  OTP_APPROVED = 'OTP_APPROVED',
  OTP_REJECTED = 'OTP_REJECTED',
  OTP_REVISION_REQUIRED = 'OTP_REVISION_REQUIRED',
  COSIGNED_PENDING = 'COSIGNED_PENDING',
  COSIGNED_UPLOADED = 'COSIGNED_UPLOADED',
  COSIGNED_APPROVED = 'COSIGNED_APPROVED',
  DEPOSIT_PENDING = 'DEPOSIT_PENDING',
  DEPOSIT_RECEIVED = 'DEPOSIT_RECEIVED',
  BOND_APPROVED = 'BOND_APPROVED',
  TRANSFER_PENDING = 'TRANSFER_PENDING',
  TRANSFER_IN_PROGRESS = 'TRANSFER_IN_PROGRESS',
  TRANSFER_REGISTERED = 'TRANSFER_REGISTERED',
  COMMISSION_PENDING = 'COMMISSION_PENDING',
  COMMISSION_PAID = 'COMMISSION_PAID',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED'
}

export enum EntityType {
  USER = 'user',
  DEVELOPMENT = 'development',
  UNIT = 'unit',
  DEAL = 'deal',
  COMMISSION = 'commission',
  SETTINGS = 'settings',
  CONTACT = 'contact',
  ACTIVITY = 'activity',
  PROPOSAL = 'proposal',
  RESERVATION = 'reservation'
}

export interface ChangeDetail {
  field: string;
  oldValue: unknown;
  newValue: unknown;
}

export interface ActivityLog {
  id: string;
  userId: string;
  userEmail: string;
  action: ActionType;
  entity: EntityType;
  entityId: string;
  entityName: string;
  changes?: ChangeDetail[];
  ipAddress: string;
  userAgent: string;
  timestamp: Timestamp;
  status: 'success' | 'failed';
  errorMessage?: string;
}

export interface SystemSettings {
  id: 'global_settings';
  vat: {
    percentage: number;
    effectiveDate: Date;
  };
  bondCommission: {
    percentage: number;
    fixedAmount?: number;
  };
  commissionStructure: {
    consultant30: number;
    independentConsultant50: number;
    partner70: number;
    viewingAgentPerViewing: number;
    viewingAgentApproved: number;
  };
  dealTimelines: {
    preReserveHours: number;
    reservedHours: number;
    awaitingOtpHours: number;
    awaitingFinancialsDays: number;
  };
  emailSettings: {
    fromName: string;
    fromEmail: string;
    inviteExpiryDays: number;
  };
  companyInfo: {
    name: string;
    logo: string;
    address: string;
    phone: string;
    email: string;
    registrationNumber: string;
    vatNumber: string;
  };
  invoiceSettings: {
    prefix: string;
    nextNumber: number;
    footer: string;
    bankDetails: {
      bankName: string;
      accountName: string;
      accountNumber: string;
      branchCode: string;
      swiftCode?: string;
    };
  };
  provinces: string[];
  updatedBy: string;
  updatedAt: Timestamp;
}

export interface DealDocument {
  id: string;
  type: 'otp' | 'otp_cosigned' | 'bond_approval' | 'transfer' | 'other';
  fileName: string;
  uploadedAt: Date | Timestamp;
  uploadedBy: string;
  downloadUrl: string;
  version: number;
  status: 'pending' | 'approved' | 'rejected';
  reviewedBy?: string;
  reviewedAt?: Date | Timestamp;
  reviewNotes?: string;
}

export interface DealStatusHistory {
  status: DealStatus;
  changedAt: Date | Timestamp;
  changedBy: string;
  notes?: string;
  documents?: string[]; // Document IDs associated with this status change
}

export interface Payment {
  id: string;
  amount: number;
  date: Date | Timestamp;
  type: 'deposit' | 'cash_payment' | 'bond_payment' | 'other';
  reference?: string;
  notes?: string;
  recordedBy: string;
  recordedAt: Date | Timestamp;
  proofOfPayment?: string; // URL to uploaded proof of payment document
  proofOfPaymentName?: string; // Original filename of the uploaded document
}

export interface DealAmount {
  salePrice: number;
  deposit: number;
  depositPaidDate?: Date | Timestamp;
  bondAmount?: number;
  bondApprovedDate?: Date | Timestamp;
  commission: number;
  bondCommission: number;
  referralFee?: number;
  vatAmount: number;
  // Payment tracking
  payments?: Payment[];
  totalPaid?: number;
  outstandingAmount?: number;
}

/**
 * Buyer race / SA population group — captured on the deal for demographic
 * reporting. "Prefer not to say" lets the buyer decline without leaving it blank.
 */
export type BuyerRace =
  | 'African'
  | 'Coloured'
  | 'Indian/Asian'
  | 'White'
  | 'Other'
  | 'Prefer not to say';

export const BUYER_RACE_OPTIONS: BuyerRace[] = [
  'African',
  'Coloured',
  'Indian/Asian',
  'White',
  'Other',
  'Prefer not to say',
];

/**
 * Buyer nationality. When `foreign`, the buyer has no 13-digit SA ID, so age/sex
 * can't be derived — they're captured manually instead.
 */
export type BuyerNationality = 'south_african' | 'foreign';

export const NATIONALITY_OPTIONS: Array<{ value: BuyerNationality; label: string }> = [
  { value: 'south_african', label: 'South African' },
  { value: 'foreign', label: 'Foreign national' },
];

/** Buyer marital status — demographic reporting + legal/transfer context. */
export type BuyerMaritalStatus =
  | 'single'
  | 'married_in_community'
  | 'married_out_of_community'
  | 'divorced'
  | 'widowed'
  | 'life_partner';

export const MARITAL_STATUS_OPTIONS: Array<{ value: BuyerMaritalStatus; label: string }> = [
  { value: 'single', label: 'Single' },
  { value: 'married_in_community', label: 'Married — in community of property' },
  { value: 'married_out_of_community', label: 'Married — out of community of property' },
  { value: 'divorced', label: 'Divorced' },
  { value: 'widowed', label: 'Widowed' },
  { value: 'life_partner', label: 'Life partner' },
];

/** Buyer intent — reuses the deal's existing `buyerCategory` ('investor' | 'end_user'). */
export const BUYER_INTENT_OPTIONS: Array<{ value: 'investor' | 'end_user'; label: string }> = [
  { value: 'investor', label: 'Investor' },
  { value: 'end_user', label: 'End user' },
];

export interface Deal {
  id: string;
  partnerId: string; // Partner/organization that owns this deal

  // References
  developmentId: string;
  developmentName: string;
  unitId: string;
  unitNumber: string;

  // Parties
  buyerName: string;
  buyerEmail: string;
  buyerPhone: string;
  buyerIdNumber?: string;
  // Buyer demographics — derived from the SA ID number (gender/DOB) or captured
  // directly. Used by the super-admin Buyers List / demographics report.
  buyerGender?: 'Male' | 'Female';
  buyerDateOfBirth?: Date | Timestamp;
  buyerRace?: BuyerRace;
  buyerNationality?: BuyerNationality;
  buyerMaritalStatus?: BuyerMaritalStatus;
  buyerCategory?: 'investor' | 'end_user'; // Buyer intent

  // Buyer type and entity details
  buyerType?: 'personal' | 'pty' | 'trust';
  entityDetails?: {
    entityName: string;
    registrationNumber: string;
    trusteeNames?: string;
    representativeName?: string;
    representativeIdNumber?: string;
  };
  
  // Connected CRM contacts
  primaryContactId?: string;
  additionalContactIds?: string[];
  
  agentId: string;
  agentName: string;
  facilitatorId?: string;
  facilitatorName?: string;
  
  // Deal details
  status: DealStatus;
  statusHistory: DealStatusHistory[];
  
  // Deal type and financing
  dealType?: 'cash' | 'finance';
  financingType?: 'cash' | 'finance';
  bondOriginator?: string;
  
  // Financial
  amounts: DealAmount;
  
  // Documents
  documents: DealDocument[];
  
  // Important dates
  reservationDate: Date | Timestamp;
  preReserveDate?: Date | Timestamp;
  otpUploadDate?: Date | Timestamp;
  otpApprovalDate?: Date | Timestamp;
  otpSellerSignDate?: Date | Timestamp;
  financialsUploadDate?: Date | Timestamp;
  aipReceivedDate?: Date | Timestamp;
  finalGrantDate?: Date | Timestamp;
  expectedTransferDate?: Date | Timestamp;
  actualTransferDate?: Date | Timestamp;
  commissionReceivedDate?: Date | Timestamp;
  
  // Timer deadlines
  timerDeadline?: Date | Timestamp; // Current timer deadline based on status
  timerHours?: number; // Hours for current timer (48 for reserved, etc)
  
  // Metadata
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
  createdBy: string;
  
  // Review & approval
  assignedReviewer?: string;
  reviewerNotes?: string;
  rejectionReason?: string;
  
  // Notifications
  lastNotificationSent?: Date | Timestamp;
  notificationsSent: string[]; // Array of notification types sent
}

// Finance Status Types for Deal Capture
export interface FinanceDocument {
  id: string;
  type: 'declined' | 'aip' | 'final_grant';
  bank?: string;
  documentUrl: string;
  documentName: string;
  note: string;
  aipAmount?: number; // Amount for AIP documents
  finalGrantAmount?: number; // Amount for Final Grant documents
  uploadedAt: Timestamp;
  uploadedBy: string;
  uploadedByName: string; // Store user name at upload time
  status: 'active' | 'superseded' | 'reassessment_superseded'; // Track if replaced by newer document or by reassessment
  reassessmentNumber?: number; // Which reassessment this document belongs to (if any)
  fileSize?: number;
  originalSize?: number;
  compressionRatio?: number;
}

export interface FinanceStatusHistory {
  id: string;
  previousStatus?: 'declined' | 'aip' | 'final_grant';
  newStatus: 'declined' | 'aip' | 'final_grant';
  reason: string;
  changedAt: Timestamp;
  changedBy: string;
  changedByName: string;
  relatedDocumentId?: string;
  bank?: string;
  /** Scoreboard Phase 0: fixed-list decline reason (P24), next to the free-text `reason`. */
  reasonCode?: string;
}

export interface ClientResponseHistory {
  id: string;
  responseType: 'accepted' | 'declined';
  bank?: string;
  grantDocumentId?: string; // Reference to the final grant being responded to
  reason?: string;
  note: string;
  documentUrl?: string;
  documentName?: string;
  respondedAt: Timestamp;
  respondedBy: string;
  respondedByName: string;

  // HQ verification fields (for accepted responses with documents)
  documentStatus?: 'pending' | 'approved' | 'rejected'; // Review status for acceptance documents
  reviewedBy?: string; // HQ user who reviewed
  reviewedByName?: string; // HQ user name
  reviewedAt?: Timestamp; // When reviewed
  rejectionReason?: string; // Reason if rejected
  rejectionHistory?: Array<{
    rejectedAt: Timestamp;
    rejectedBy: string;
    rejectedByName: string;
    reason: string;
    version: number;
  }>; // Track all rejections with version numbers
}

// Helper type for creating new deals
export type CreateDealInput = Omit<Deal, 'id' | 'createdAt' | 'updatedAt' | 'statusHistory' | 'documents' | 'notificationsSent'>;